import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, ArrowUpRight } from "lucide-react";

const COLORS = { ok: "#10b981", offline: "#f59e0b", alarm: "#ef4444" };

function dropIcon(status) {
  const color = COLORS[status] || COLORS.offline;
  const pulse = status === "alarm"
    ? `<span style="position:absolute;left:50%;top:38%;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:50%;background:${color};opacity:0.35;animation:dcpulse 1.4s ease-out infinite;"></span>`
    : "";
  const html = `
    <div style="position:relative;width:40px;height:48px;">
      ${pulse}
      <svg width="40" height="48" viewBox="0 0 40 48" style="position:relative;filter:drop-shadow(0 3px 4px rgba(15,23,42,0.35));">
        <path d="M20 2 C20 2 4 20 4 30 a16 16 0 0 0 32 0 C36 20 20 2 20 2 Z"
          fill="${color}" stroke="#ffffff" stroke-width="2.5"/>
        <circle cx="18.5" cy="31.5" r="7" fill="#ffffff"/>
        <circle cx="22" cy="28.8" r="7" fill="${color}"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "dosatronic-drop", iconSize: [40, 48], iconAnchor: [20, 46], popupAnchor: [0, -44] });
}

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.id).join(",");
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [60, 60], maxZoom: 13 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export default function MapView() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [stations, setStations] = useState([]);

  const LABELS = { ok: t("map.allOk"), offline: t("map.noConnection"), alarm: t("map.alarmActive") };

  const load = async () => {
    const { data } = await api.get("/stations-map");
    setStations(data);
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  const pts = useMemo(() => stations.filter((s) => s.lat != null && s.lng != null), [stations]);
  const center = pts.length ? [pts[0].lat, pts[0].lng] : [41.38, 2.17];
  const counts = useMemo(() => {
    const c = { ok: 0, offline: 0, alarm: 0 };
    stations.forEach((s) => { c[s.map_status] = (c[s.map_status] || 0) + 1; });
    return c;
  }, [stations]);

  const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : t("map.noSignal"));

  return (
    <Layout>
      <style>{`@keyframes dcpulse{0%{transform:translate(-50%,-50%) scale(0.6);opacity:0.5}100%{transform:translate(-50%,-50%) scale(2.2);opacity:0}}`}</style>
      <PageHeader
        title={t("map.title")}
        subtitle={t("map.subtitle")}
        actions={
          <Button variant="outline" onClick={load} data-testid="refresh-map">
            <RefreshCw className="mr-2 h-4 w-4" /> {t("common.refresh")}
          </Button>
        }
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-4" data-testid="map-legend">
          {["ok", "alarm", "offline"].map((k) => (
            <div key={k} className="flex items-center gap-2 text-sm text-slate-600">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: COLORS[k] }} />
              {LABELS[k]} <span className="font-mono text-slate-400">({counts[k] || 0})</span>
            </div>
          ))}
        </div>

        <Card className="overflow-hidden border-slate-200 shadow-none" data-testid="stations-map">
          <MapContainer center={center} zoom={11} style={{ height: "560px", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={pts} />
            {pts.map((s) => (
              <Marker key={s.id} position={[s.lat, s.lng]} icon={dropIcon(s.map_status)}>
                <Popup>
                  <div className="min-w-[220px]" data-testid={`map-popup-${s.code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                        <div className="font-mono text-xs text-slate-400">{s.code}</div>
                      </div>
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                        style={{ background: COLORS[s.map_status] }}>
                        {LABELS[s.map_status]}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                      <span className="text-slate-400">{t("map.rssi")}</span>
                      <span className="text-right font-mono">{s.rssi != null ? `${s.rssi} dBm` : "—"}</span>
                      <span className="text-slate-400">{t("map.power")}</span>
                      <span className="text-right">{s.power || "—"}</span>
                      <span className="text-slate-400">{t("map.alarms")}</span>
                      <span className="text-right font-mono">{s.active_alarms}</span>
                      <span className="text-slate-400">{t("map.lastConnection")}</span>
                      <span className="text-right">{fmt(s.last_seen)}</span>
                    </div>
                    {Object.keys(s.latest || {}).length > 0 && (
                      <div className="mt-2 border-t border-slate-100 pt-2">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t("map.lastValues")}</div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          {Object.entries(s.latest).map(([name, v]) => (
                            <React.Fragment key={name}>
                              <span className="text-slate-500">{name}</span>
                              <span className="text-right font-mono text-slate-800">
                                {v.type === "bool" ? (v.value >= 0.5 ? "ON" : "OFF") : v.value} {v.unit}
                              </span>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => navigate(`/stations/${s.id}`)}
                      data-testid={`map-detail-${s.code}`}
                      className="mt-3 flex w-full items-center justify-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                    >
                      {t("common.viewDetail")} <ArrowUpRight className="h-3 w-3" />
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Card>
      </div>
    </Layout>
  );
}
